import { observer } from 'mobx-react-lite';
import { Factory, Play, Question, ShieldCheck, WarningOctagon } from '@phosphor-icons/react';
import { useResolve } from '@/infrastructure/useResolve';
import { useT } from './useT';
import { TOKEN_ImportsStore } from './ImportsStore';
import { TOKEN_OnboardingStore } from './OnboardingStore';
import { TOKEN_SimulationStore } from './SimulationStore';
import { ImportSlotView } from './transient/ImportSlotView';
import { ImportMappingView } from './transient/ImportMappingView';
import { HelpView } from './HelpView';
import { LocaleSwitcherView } from './LocaleSwitcherView';
import { SettingsView } from './SettingsView';
import { ResultsView } from './ResultsView';
import { ProcessingView } from './ProcessingView';
import { WarningsView } from './WarningsView';
import { ToastView } from './ToastView';
import { ThemeToggleView } from './ThemeToggleView';

const StatusBar = observer(function StatusBar() {
  const imports = useResolve(TOKEN_ImportsStore);
  const sim = useResolve(TOKEN_SimulationStore);
  const t = useT();
  const ready = imports.isReadyToRun;
  const errored = sim.phase === 'error';
  const led = errored ? 'text-light-red' : ready ? 'text-light-green' : 'text-light-amber';
  const label = errored ? t.status.fault : ready ? t.status.ready : t.status.awaitingInputs;
  return (
    <div className="flex items-center gap-2 border-b border-steel-300 bg-steel-100 px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-steel-600 dark:border-steel-800 dark:bg-steel-900 dark:text-steel-400">
      <span className={`led ${led}`} />
      <span>{t.status.label}</span>
      <span className="font-bold text-steel-900 dark:text-steel-100">{label}</span>
    </div>
  );
});

export const AppView = observer(function AppView() {
  const imports = useResolve(TOKEN_ImportsStore);
  const sim = useResolve(TOKEN_SimulationStore);
  const onboarding = useResolve(TOKEN_OnboardingStore);
  const t = useT();

  const showResults = sim.hasResult;
  const showProcessing = sim.isRunning;
  const showSetup = !showResults && !showProcessing;

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between border-b-2 border-steel-300 bg-white px-4 py-2 dark:border-steel-700 dark:bg-steel-900">
        <div className="flex items-center gap-3">
          <Factory weight="fill" size={26} className="text-amber-signal" />
          <div className="flex items-baseline gap-2">
            <h1 className="font-mono text-lg font-bold tracking-tight">beforered</h1>
            <span className="font-mono text-[10px] uppercase tracking-widest text-steel-500">
              {t.brand.tagline}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onboarding.openHelp}
            aria-label={t.help.open}
            title={t.help.open}
            className="flex h-8 w-8 items-center justify-center border border-steel-300 bg-white text-steel-700 hover:border-amber-signal hover:text-amber-signal dark:border-steel-700 dark:bg-steel-900 dark:text-steel-300"
          >
            <Question weight="bold" size={16} />
          </button>
          <LocaleSwitcherView />
          <ThemeToggleView />
        </div>
      </header>

      <StatusBar />

      <main className="flex min-h-0 flex-1 flex-col">
        {showSetup && (
          <div className="flex flex-col gap-5 p-6">
            <div className="max-w-2xl border-l-4 border-steel-400 bg-white p-3 shadow-sm dark:bg-steel-900">
              <p className="text-sm leading-relaxed text-steel-700 dark:text-steel-200">
                {t.setup.intro}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <ImportSlotView store={imports.bom} />
              <ImportSlotView store={imports.inventory} />
              <ImportSlotView store={imports.sales} />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <ImportSlotView store={imports.mfg} />
              <ImportSlotView store={imports.purchase} />
              <ImportSlotView store={imports.parts} />
            </div>

            <div className="flex flex-col gap-3">
              {(['BOM', 'INVENTORY', 'SALES', 'MFG', 'PURCHASE', 'PARTS'] as const).map(
                (source) => (
                  <ImportMappingView key={source} store={imports.slot(source)} />
                ),
              )}
            </div>

            <SettingsView />

            <div className="flex items-center gap-4">
              <div className="hazard-border p-1">
                <button
                  type="button"
                  onClick={() => void sim.run()}
                  disabled={!imports.isReadyToRun}
                  className="flex items-center gap-2 bg-amber-signal px-6 py-2.5 font-mono text-sm font-bold uppercase tracking-widest text-steel-950 transition hover:bg-white disabled:bg-steel-200 disabled:text-steel-600 dark:disabled:bg-steel-800 dark:disabled:text-steel-300"
                >
                  <Play weight="fill" size={16} />
                  {t.setup.runEngine}
                </button>
              </div>
              {imports.isReadyToRun ? (
                <span className="flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-light-green-deep dark:text-light-green">
                  <ShieldCheck weight="fill" size={14} />
                  {t.status.allInputsLoaded}
                </span>
              ) : (
                <span className="font-mono text-xs text-steel-700 dark:text-steel-300">
                  {t.status.requiredInputs}
                </span>
              )}
            </div>

            {sim.phase === 'error' && (
              <div className="flex items-start gap-2 border-l-4 border-light-red bg-light-red/10 p-3 text-xs">
                <WarningOctagon
                  weight="fill"
                  className="mt-0.5 text-light-red-deep dark:text-light-red"
                  size={16}
                />
                <div className="font-mono">
                  <strong className="text-light-red-deep dark:text-light-red">
                    {t.setup.fault}
                  </strong>{' '}
                  {sim.errorMessage}
                </div>
              </div>
            )}
          </div>
        )}

        {showProcessing && <ProcessingView />}
        {showResults && <ResultsView />}
      </main>

      <WarningsView />
      <HelpView />
      <ToastView />
    </div>
  );
});
